import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

type CTA = { label: string; to: string; search?: Record<string, string> };

type Props = {
  headline: ReactNode;
  body: ReactNode;
  primary: CTA;
  secondary?: CTA;
  variant?: "light" | "deep";
};

export function CTABlock({ headline, body, primary, secondary, variant = "light" }: Props) {
  const isDeep = variant === "deep";
  const wrap = isDeep
    ? "rounded-lg border border-white/10 bg-white/5 p-8 md:p-12"
    : "rounded-lg border border-rule bg-white p-8 md:p-12";
  const head = isDeep ? "text-white" : "text-ink";
  const text = isDeep ? "text-white/85" : "text-ink-3";
  const secondaryColor = isDeep ? "text-white/85 hover:text-white" : "text-cyan-deep hover:underline";

  return (
    <div className={wrap}>
      <div className="max-w-2xl">
        <h3 className={`font-display text-3xl leading-snug md:text-4xl ${head}`}>{headline}</h3>
        <p className={`mt-5 leading-relaxed ${text}`}>{body}</p>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-6">
        <Link
          to={primary.to}
          search={primary.search as never}
          className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90"
        >
          {primary.label} →
        </Link>
        {secondary && (
          <Link
            to={secondary.to}
            search={secondary.search as never}
            className={`font-mono text-xs uppercase tracking-wider ${secondaryColor}`}
          >
            {secondary.label} →
          </Link>
        )}
      </div>
    </div>
  );
}

